import React, { JSX, useLayoutEffect, useState } from 'react'
import { MARQUEE_CSS, MarqueeBar, hexToRgba } from 'components/MerchTickerMarquee'
import { chatFontFamily } from 'lib/chatFonts'
import type { TickerBarBarStyle, TickerBarMessage, TickerBarStyle } from 'types/api'

export const TICKER_BAR_MARQUEE_WIDTH = 1920

export const TICKER_BAR_STAFF_FONT_KEY = 'VCR_OSD_MONO'
export const TICKER_BAR_DIVIDER_MODES = ['AFTER_LABEL', 'AFTER_MESSAGE', 'OFF'] as const

export const TICKER_BAR_SHARED_DEFAULTS: Omit<TickerBarStyle, 'top' | 'bottom'> = {
  showTopBar: true,
  scrollSpeedPxS: 160,
  borderColor: '#ffffff',
  borderWidth: 0,
  gapHeight: 25,
  gapColor: 'transparent',
  pageBackground: 'transparent',
  textGlowPx: 0,
  textGlowColor: '#ffffff40'
}

export const TICKER_BAR_BAR_DEFAULTS: TickerBarBarStyle = {
  bgColor: '#000000',
  opacity: 1,
  padTop: 5,
  padBottom: 5,
  outerPad: 0,
  lineHeightPx: 20,
  separatorGapPx: null,
  scrollReverse: false,
  dividerMode: 'AFTER_LABEL',
  labelGapPx: 12,
  labelFontKey: TICKER_BAR_STAFF_FONT_KEY,
  labelFontWeight: 400,
  labelSizePx: 20,
  labelColor: '#3ddc5a',
  textFontKey: TICKER_BAR_STAFF_FONT_KEY,
  textFontWeight: 400,
  textSizePx: 20,
  textColor: '#ffffff',
  logoOn: false,
  logoUrl: null,
  logoHeightPx: 24,
  logoGapPx: 16,
  logoEdgePadPx: 8
}

export const TICKER_BAR_BAR_STYLE_KEYS = Object.keys(TICKER_BAR_BAR_DEFAULTS) as Array<keyof TickerBarBarStyle>
export const TICKER_BAR_SHARED_STYLE_KEYS = Object.keys(TICKER_BAR_SHARED_DEFAULTS) as Array<keyof Omit<TickerBarStyle, 'top' | 'bottom'>>

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v)

const keyOr = (src: Record<string, unknown>, key: string, fallback: unknown): unknown => {
  const v = src[key]
  if (v == null) return fallback
  if (typeof fallback === 'number' || (fallback == null && key === 'separatorGapPx')) {
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback
  }
  if (typeof fallback === 'boolean') return typeof v === 'boolean' ? v : fallback
  return typeof v === 'string' ? v : fallback
}

const resolveBar = (raw: unknown, fontKey: string): TickerBarBarStyle => {
  const src = isRecord(raw) ? raw : {}
  const out: Record<string, unknown> = {}
  for (const key of TICKER_BAR_BAR_STYLE_KEYS) {
    const fallback = key === 'labelFontKey' || key === 'textFontKey' ? fontKey : TICKER_BAR_BAR_DEFAULTS[key]
    out[key] = keyOr(src, key, fallback)
  }
  return out as unknown as TickerBarBarStyle
}

export const resolveTickerBarStyle = (raw: unknown, fontKey: string = TICKER_BAR_STAFF_FONT_KEY): TickerBarStyle => {
  const src = isRecord(raw) ? raw : {}
  const shared: Record<string, unknown> = {}
  for (const key of TICKER_BAR_SHARED_STYLE_KEYS) shared[key] = keyOr(src, key, TICKER_BAR_SHARED_DEFAULTS[key])
  return {
    ...(shared as unknown as Omit<TickerBarStyle, 'top' | 'bottom'>),
    top: resolveBar(src.top, fontKey),
    bottom: resolveBar(src.bottom, fontKey)
  }
}

export const resolveTickerBarMessages = (raw: unknown, prefix: 't' | 'b'): TickerBarMessage[] => {
  if (!Array.isArray(raw)) return []
  const out: TickerBarMessage[] = []
  raw.forEach((m: unknown, i) => {
    if (!isRecord(m) || typeof m.text !== 'string') return
    out.push({
      id: typeof m.id === 'string' && m.id !== '' ? m.id : `${prefix}${i}`,
      label: typeof m.label === 'string' ? m.label : '',
      labelColor: typeof m.labelColor === 'string' && m.labelColor !== '' ? m.labelColor : null,
      text: m.text
    })
  })
  return out
}

export interface TickerBarMarqueeInput {
  style: TickerBarStyle
  topMessages: TickerBarMessage[]
  bottomMessages: TickerBarMessage[]
}

export interface TickerBarEnvelope extends TickerBarMarqueeInput {
  kind: 'TICKER_BAR'
  tickerBarId: string
  eventId: string | null
  version: number
  updatedAt: string | null
  restart: boolean
}

export interface TickerBarDeletionEnvelope {
  kind: 'TICKER_BAR'
  tickerBarId: string
  eventId: string | null
  version: number
  deleted: true
}

export type TickerBarFrame = TickerBarEnvelope | TickerBarDeletionEnvelope

export const isTickerBarDeletion = (frame: TickerBarFrame): frame is TickerBarDeletionEnvelope =>
  (frame as Partial<TickerBarDeletionEnvelope>).deleted === true

export const parseTickerBarEnvelope = (raw: unknown): TickerBarFrame | null => {
  let obj: unknown = raw
  for (let unwraps = 0; typeof obj === 'string' && unwraps < 2; unwraps++) {
    try {
      obj = JSON.parse(obj)
    } catch {
      return null
    }
  }
  if (!isRecord(obj) || obj.kind !== 'TICKER_BAR') return null
  if (typeof obj.tickerBarId !== 'string' || obj.tickerBarId === '') return null
  const version = obj.version
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 0) return null
  const eventId = typeof obj.eventId === 'string' ? obj.eventId : null
  if (obj.deleted === true) {
    return { kind: 'TICKER_BAR', tickerBarId: obj.tickerBarId, eventId, version, deleted: true }
  }
  return {
    kind: 'TICKER_BAR',
    tickerBarId: obj.tickerBarId,
    eventId,
    version,
    updatedAt: typeof obj.updatedAt === 'string' ? obj.updatedAt : null,
    style: resolveTickerBarStyle(obj.style),
    topMessages: resolveTickerBarMessages(obj.topMessages, 't'),
    bottomMessages: resolveTickerBarMessages(obj.bottomMessages, 'b'),
    restart: obj.restart === true
  }
}

export const tickerBarBarHeightOf = (bar: TickerBarBarStyle, borderWidth: number): number =>
  bar.outerPad + 2 * borderWidth + bar.padTop + bar.lineHeightPx + bar.padBottom

export const tickerBarHeightOf = (style: TickerBarStyle): number => {
  const bottom = tickerBarBarHeightOf(style.bottom, style.borderWidth)
  if (!style.showTopBar) return bottom
  return tickerBarBarHeightOf(style.top, style.borderWidth) + style.gapHeight + bottom
}

export interface LogoNaturalSize {
  width: number
  height: number
}

export interface TickerBarLogoBox {
  width: number
  height: number
  shrunk: boolean
}

export const tickerBarLogoBoxOf = (
  bar: TickerBarBarStyle,
  natural: LogoNaturalSize | null,
  wrapWidth: number
): TickerBarLogoBox | null => {
  if (!bar.logoOn || bar.logoUrl == null || natural == null) return null
  if (!(natural.width > 0) || !(natural.height > 0)) return null
  const rowBox = bar.padTop + bar.lineHeightPx + bar.padBottom
  let height = Math.min(bar.logoHeightPx, rowBox)
  let width = (height * natural.width) / natural.height
  const maxWidth = wrapWidth / 2
  if (width > maxWidth) {
    width = maxWidth
    height = (width * natural.height) / natural.width
  }
  return { width, height, shrunk: height < bar.logoHeightPx }
}

export const tickerBarLogoReserveOf = (bar: TickerBarBarStyle, box: TickerBarLogoBox | null): number =>
  box == null ? 0 : box.width + bar.logoGapPx + bar.logoEdgePadPx

export const tickerBarDividerClassOf = (mode: string): string =>
  mode === 'AFTER_MESSAGE' ? 'mq-div-message' : mode === 'OFF' ? 'mq-div-off' : 'mq-div-label'

export const tickerBarMetricsSigOf = (bar: TickerBarBarStyle, fontEpoch: number): string =>
  [
    bar.labelFontKey,
    bar.labelFontWeight,
    bar.labelSizePx,
    bar.textFontKey,
    bar.textFontWeight,
    bar.textSizePx,
    bar.labelGapPx,
    bar.separatorGapPx ?? 'auto',
    bar.dividerMode,
    fontEpoch
  ].join(':')

const SYSTEM_FONT_STACK = 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'

export const tickerBarFontStack = (key: string): string => chatFontFamily(key) ?? SYSTEM_FONT_STACK

export const TICKER_BAR_CSS = `
.merch-marquee.mq-gen .mq-gside { position: relative; }
.merch-marquee.mq-gen .mq-gouter { height: var(--tb-outer-pad, 0px); background: var(--tb-bg, #000000); }
.merch-marquee.mq-gen .mq-glane { position: relative; box-sizing: content-box; height: var(--tb-lh, 20px); padding: var(--tb-pad-top, 5px) 0 var(--tb-pad-bottom, 5px); background: var(--tb-bg, #000000); border: 0 solid var(--tb-border-color, #ffffff); border-top-width: var(--tb-border-w, 0px); border-bottom-width: var(--tb-border-w, 0px); }
.merch-marquee.mq-gen .mq-ggap { height: var(--tb-gap-h, 25px); background: var(--tb-gap-bg, transparent); }
.merch-marquee.mq-gen .mq-gclip { position: relative; height: var(--tb-lh, 20px); }
.merch-marquee.mq-gen .mq-bar { height: var(--tb-lh, 20px); border-width: 0; }
.merch-marquee.mq-gen .mq-copy { font-family: var(--tb-text-font); font-size: var(--tb-text-size, 20px); font-weight: var(--tb-text-weight, 400); line-height: var(--tb-lh, 20px); color: var(--tb-text-color, #ffffff); transform: none; }
.merch-marquee.mq-gen .mq-gmsg { display: inline-flex; align-items: center; vertical-align: top; height: var(--tb-lh, 20px); padding-right: var(--tb-sepgap, 1.5em); }
.merch-marquee.mq-gen .mq-gmsg > span { line-height: 1; }
.merch-marquee.mq-gen .mq-glabel { font-family: var(--tb-label-font); font-size: var(--tb-label-size, 20px); font-weight: var(--tb-label-weight, 400); color: var(--tb-label-color, #3ddc5a); }
.merch-marquee.mq-gen .mq-gdiv { padding: 0 var(--tb-sepgap, 1.5em); }
.merch-marquee.mq-gen .mq-div-message .mq-gmsg { padding-right: 0; }
.merch-marquee.mq-gen .mq-div-label .mq-gdiv-m, .merch-marquee.mq-gen .mq-div-message .mq-gdiv-l, .merch-marquee.mq-gen .mq-div-off .mq-gdiv { display: none; }
.merch-marquee.mq-gen .mq-div-message .mq-glabel, .merch-marquee.mq-gen .mq-div-off .mq-glabel { margin-right: var(--tb-label-gap, 12px); }
.merch-marquee.mq-gen .mq-gnolabel .mq-glabel, .merch-marquee.mq-gen .mq-gnolabel .mq-gdiv-l { display: none; }
.merch-marquee.mq-gen .mq-glogo { position: absolute; top: 50%; right: var(--tb-logo-edge, 8px); transform: translateY(-50%); display: block; max-width: none; }
.merch-marquee.mq-gen .mq-ghint { position: absolute; top: 0; left: 0; height: var(--tb-lh, 20px); padding-left: 8px; font-family: var(--tb-text-font); font-size: var(--tb-text-size, 20px); line-height: var(--tb-lh, 20px); color: var(--tb-text-color, #ffffff); opacity: 0.6; white-space: pre; pointer-events: none; }
`

const messageId = (m: TickerBarMessage): string => m.id
const messageStructure = (): string => ''
const messageContent = (m: TickerBarMessage): string =>
  JSON.stringify([m.label, m.labelColor, m.text])

const NO_DELTAS: Record<string, number> = {}
const NO_TWEEN_MS = 600

const renderMessages = (messages: TickerBarMessage[]): JSX.Element => (
  <>
    {messages.map((m) => (
      <span key={m.id} className={m.label === '' ? 'mq-gmsg mq-gnolabel' : 'mq-gmsg'} data-mq-id={m.id}>
        <span className='mq-glabel' style={m.labelColor != null ? { color: m.labelColor } : undefined}>{m.label}</span>
        <span className='mq-gdiv mq-gdiv-l'>&#x2F;&#x2F;</span>
        <span className='mq-gtext'>{m.text}</span>
        <span className='mq-gdiv mq-gdiv-m'>&#x2F;&#x2F;</span>
      </span>
    ))}
  </>
)

export type TickerBarLogoSizes = Readonly<Record<string, LogoNaturalSize | 'failed'>>

const useLogoNatural = (url: string | null, seed: LogoNaturalSize | 'failed' | undefined): LogoNaturalSize | null => {
  const [probed, setProbed] = useState<{ url: string, size: LogoNaturalSize | null } | null>(null)
  useLayoutEffect(() => {
    if (url == null || seed != null || typeof globalThis.Image === 'undefined') return
    let cancelled = false
    const img = new globalThis.Image()
    const settle = (): void => {
      if (cancelled) return
      const size = img.naturalWidth > 0 && img.naturalHeight > 0
        ? { width: img.naturalWidth, height: img.naturalHeight }
        : null
      setProbed((prev) => (prev != null && prev.url === url && prev.size?.width === size?.width && prev.size?.height === size?.height
        ? prev
        : { url, size }))
    }
    img.onload = settle
    img.onerror = settle
    img.src = url
    if (img.complete && img.naturalWidth > 0) settle()
    return () => {
      cancelled = true
      img.onload = null
      img.onerror = null
    }
  }, [url, seed])
  if (url == null) return null
  if (seed != null) return seed === 'failed' ? null : seed
  return probed != null && probed.url === url ? probed.size : null
}

const TickerBarSide = ({
  which,
  bar,
  messages,
  wrapWidth,
  speedPxS,
  fontEpoch,
  restartKey,
  logoSizes,
  emptyBarHint
}: {
  which: 'top' | 'bottom'
  bar: TickerBarBarStyle
  messages: TickerBarMessage[]
  wrapWidth: number
  speedPxS: number
  fontEpoch: number
  restartKey: number
  logoSizes: TickerBarLogoSizes | undefined
  emptyBarHint: string | undefined
}): JSX.Element => {
  const logoUrl = bar.logoOn && bar.logoUrl != null && bar.logoUrl !== '' ? bar.logoUrl : null
  const natural = useLogoNatural(logoUrl, logoUrl == null ? undefined : logoSizes?.[logoUrl])
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const box = logoUrl != null && failedUrl !== logoUrl ? tickerBarLogoBoxOf(bar, natural, wrapWidth) : null
  const reserve = tickerBarLogoReserveOf(bar, box)
  const vars: Record<string, string> = {
    '--tb-bg': hexToRgba(bar.bgColor, bar.opacity),
    '--tb-pad-top': `${bar.padTop}px`,
    '--tb-pad-bottom': `${bar.padBottom}px`,
    '--tb-outer-pad': `${bar.outerPad}px`,
    '--tb-lh': `${bar.lineHeightPx}px`,
    '--tb-label-font': tickerBarFontStack(bar.labelFontKey),
    '--tb-label-weight': `${bar.labelFontWeight}`,
    '--tb-label-size': `${bar.labelSizePx}px`,
    '--tb-label-color': bar.labelColor,
    '--tb-text-font': tickerBarFontStack(bar.textFontKey),
    '--tb-text-weight': `${bar.textFontWeight}`,
    '--tb-text-size': `${bar.textSizePx}px`,
    '--tb-text-color': bar.textColor,
    '--tb-label-gap': `${bar.labelGapPx}px`,
    '--tb-logo-edge': `${bar.logoEdgePadPx}px`
  }
  if (bar.separatorGapPx != null) vars['--tb-sepgap'] = `${bar.separatorGapPx}px`
  const lane = (
    <div className='mq-glane'>
      <div
        className='mq-gclip'
        data-tb-reserve={reserve}
        style={reserve > 0 ? { clipPath: `inset(0 ${reserve}px 0 0)` } : undefined}
      >
        <MarqueeBar
          key={restartKey}
          barClass={`mq-gbar ${tickerBarDividerClassOf(bar.dividerMode)}`}
          wrapWidth={wrapWidth}
          speedPxS={speedPxS}
          reverse={bar.scrollReverse}
          metricsSig={tickerBarMetricsSigOf(bar, fontEpoch)}
          items={messages}
          idOf={messageId}
          structureOf={messageStructure}
          contentOf={messageContent}
          flashHint={NO_DELTAS}
          changeAnimation='NONE'
          tweenMs={NO_TWEEN_MS}
          renderCopy={renderMessages}
        />
        {emptyBarHint != null && messages.length === 0 && (
          <span className='mq-ghint' aria-hidden='true'>{emptyBarHint}</span>
        )}
      </div>
      {box != null && logoUrl != null && (
        <img
          className='mq-glogo'
          src={logoUrl}
          alt=''
          draggable={false}
          style={{ width: `${box.width}px`, height: `${box.height}px` }}
          onError={() => setFailedUrl(logoUrl)}
        />
      )}
    </div>
  )
  return (
    <div className={`mq-gside mq-gside-${which}`} data-tb-bar={which} style={vars as React.CSSProperties}>
      {which === 'top' && <div className='mq-gouter' />}
      {lane}
      {which === 'bottom' && <div className='mq-gouter' />}
    </div>
  )
}

export const TickerBarMarquee = ({
  envelope,
  wrapWidth,
  restartKey = 0,
  logoSizes,
  emptyBarHint
}: {
  envelope: TickerBarMarqueeInput
  wrapWidth?: number
  restartKey?: number
  logoSizes?: TickerBarLogoSizes
  emptyBarHint?: string
}): JSX.Element => {
  const w = wrapWidth ?? TICKER_BAR_MARQUEE_WIDTH
  const style = envelope.style
  const speed = Number.isFinite(style.scrollSpeedPxS) && style.scrollSpeedPxS > 0
    ? style.scrollSpeedPxS
    : TICKER_BAR_SHARED_DEFAULTS.scrollSpeedPxS
  const [fontEpoch, setFontEpoch] = useState(0)
  useLayoutEffect(() => {
    if (typeof globalThis.document === 'undefined' || globalThis.document.fonts == null) return
    const fonts = globalThis.document.fonts
    const bump = (): void => setFontEpoch((e) => e + 1)
    fonts.addEventListener('loadingdone', bump)
    let disposed = false
    void fonts.ready.then(() => {
      if (!disposed) bump()
    })
    return () => {
      disposed = true
      fonts.removeEventListener('loadingdone', bump)
    }
  }, [])
  const rootVars: Record<string, string> = {
    '--mq-wrap': `${w}px`,
    '--tb-border-w': `${style.borderWidth}px`,
    '--tb-border-color': style.borderColor,
    '--tb-gap-h': `${style.gapHeight}px`,
    '--tb-gap-bg': style.gapColor
  }
  if (style.textGlowPx > 0) rootVars['--mq-text-glow'] = `0 0 ${style.textGlowPx}px ${style.textGlowColor}`
  const side = (which: 'top' | 'bottom'): JSX.Element => (
    <TickerBarSide
      which={which}
      bar={style[which]}
      messages={which === 'top' ? envelope.topMessages : envelope.bottomMessages}
      wrapWidth={w}
      speedPxS={speed}
      fontEpoch={fontEpoch}
      restartKey={restartKey}
      logoSizes={logoSizes}
      emptyBarHint={emptyBarHint}
    />
  )
  return (
    <div
      className='merch-marquee mq-gen'
      data-testid='merch-ticker-marquee'
      style={rootVars as React.CSSProperties}
    >
      <style>{MARQUEE_CSS}</style>
      <style>{TICKER_BAR_CSS}</style>
      {style.showTopBar && side('top')}
      {style.showTopBar && <div className='mq-ggap' />}
      {side('bottom')}
    </div>
  )
}
